import {
  Form,
  Input,
  Button,
  Card,
  Typography,
  App,
  Upload,
  Select,
  InputNumber,
  Spin,
  Row,
  Col,
  Space,
  Tag,
  Modal,
} from "antd";
import API from "../../../../services/api";
import { uploadToCloudinary, uploadMultipleToCloudinary } from "../../../../utils/uploadToCloudinary";
import { useEffect, useState, useRef, useCallback } from "react";
import LocationField from "../../../LocationField";
import { useNavigate, useParams } from "react-router-dom";
import {
  SaveOutlined,
  ArrowLeftOutlined,
  ExclamationCircleOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import "./EditFranchise.css";

const { Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const CATEGORY_OPTIONS = [
  "food",
  "retail",
  "education",
  "fitness",
  "healthcare",
  "beauty",
  "technology",
  "other",
];

function EditFranchise() {
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError] = useState(false);
  const [current, setCurrent] = useState(0);
  const [autoDetecting, setAutoDetecting] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [removedImages, setRemovedImages] = useState([]);
  const [stepChanging, setStepChanging] = useState(false);
  const [removedThumbnail, setRemovedThumbnail] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);

  // Custom category (mirrors AddFranchise)
  const [customCategory, setCustomCategory] = useState("");
  const [showCustomCategory, setShowCustomCategory] = useState(false);

  // Image state
  const [thumbnailList, setThumbnailList] = useState([]);
  const [fileList, setFileList] = useState([]);

  const abortControllerRef = useRef(null);
  const [isFormDirty, setIsFormDirty] = useState(false);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Same step visibility logic as AddFranchise
  const getStepStyle = (step) => ({
    display: current === step ? "block" : "none",
    width: "100%",
  });

  // Same steps as AddFranchise
  const steps = [
    "Brand",
    "Financial",
    "Location",
    "Contact",
    "Branches",
    "Images",
  ];

  const stepFields = [
    ["brandName", "category", "description"],
    ["investmentRequired", "franchiseFee", "royaltyFee", "areaRequired", "outletCount"],
    [
      "locality",
      "city",
      "state",
      "country",
      "pincode",
      "latitude",
      "longitude",
    ],
    ["contactName", "phone", "email", "website"],
    ["branches"],
    ["thumbnail", "images"],
  ];

  // ── Image upload helpers ─────────────────────────────────────────────────
  const normalizeUpload = (e) => e?.fileList || [];

  const createUploadFile = (file, index = 0) => {
    if (!file) return null;
    if (typeof file === "string") {
      return {
        uid: `existing-${index}-${Date.now()}`,
        name: `image-${index + 1}`,
        status: "done",
        url: file,
      };
    }
    return {
      uid: file._id || file.uid || `existing-${index}-${Date.now()}`,
      name: file.name || `image-${index + 1}`,
      status: "done",
      url: file.url || file.path || file.location || file.secure_url,
    };
  };

  // ── Step navigation — same logic as AddFranchise ─────────────────────────
  const next = async () => {
    // Custom category check (step 0)
    if (current === 0) {
      if (showCustomCategory && (!customCategory || customCategory.trim() === "")) {
        message.error("Please enter custom category");
        return;
      }
    }

    // Images check (step 5)
    if (current === 5) {
      if (thumbnailList.length === 0) {
        message.error("Please upload a thumbnail image");
        return;
      }
      if (fileList.length === 0) {
        message.error("Please upload at least one image");
        return;
      }
    }

    try {
      await form.validateFields(stepFields[current]);
      setCurrent(current + 1);
      window.scrollTo(0, 0);
    } catch (err) {
      const firstError =
        err.errorFields?.[0]?.errors?.[0] || "Please fill all required fields";
      message.error({ content: firstError, key: "validation-error", duration: 2.5 });
    }
  };

  const prev = () => {
    setCurrent(current - 1);
    window.scrollTo(0, 0);
  };

  const goToStep = useCallback(
    (stepIndex) => {
      if (stepIndex >= 0 && stepIndex < steps.length) {
        setStepChanging(true);
        setCurrent(stepIndex);
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: "smooth" });
          setStepChanging(false);
        }, 100);
      }
    },
    [steps.length]
  );

  const handleKeyDown = (e, callback) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      callback();
    }
  };

  // ── Parse branches text to/from array (same format as AddFranchise) ──────
  const branchesToText = (branches) => {
    if (!Array.isArray(branches) || branches.length === 0) return "";
    return branches
      .map((b) => `${b.branchName || ""}, ${b.city || ""}, ${b.state || ""}`)
      .join("\n");
  };

  // ── Fetch existing franchise data ─────────────────────────────────────────
  const fetchFranchise = async () => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();

    try {
      setPageLoading(true);
      setPageError(false);
      const res = await API.get(`/franchises/${id}`, {
        signal: abortControllerRef.current.signal,
      });
      const item = res.data?.data || res.data;



      const thumbnailFileList = item?.thumbnail
        ? [createUploadFile(item.thumbnail, 0)].filter(Boolean)
        : [];

      const imagesFileList = Array.isArray(item?.images)
        ? item.images.map((img, index) => createUploadFile(img, index)).filter(Boolean)
        : [];

      setThumbnailList(thumbnailFileList);
      setFileList(imagesFileList);

      // Restore custom category if needed
      const cat = item?.franchiseDetails?.category || "";
      if (cat && !CATEGORY_OPTIONS.includes(cat)) {
        setShowCustomCategory(true);
        setCustomCategory(cat);
      }

      form.setFieldsValue({
        brandName: item?.franchiseDetails?.brandName || "",
        category: item?.franchiseDetails?.category || undefined,
        description: item?.description || "",
        investmentRequired: item?.franchiseDetails?.investmentRequired ?? undefined,
        franchiseFee: item?.franchiseDetails?.franchiseFee ?? undefined,
        royaltyFee: item?.franchiseDetails?.royaltyFee ?? undefined,
        areaRequired: item?.franchiseDetails?.areaRequired ?? undefined,
        outletCount: item?.franchiseDetails?.outletCount ?? undefined,
        locality:
          item?.headquarters?.locality || "",
        city: item?.headquarters?.city || "",
        state: item?.headquarters?.state || "",
        country: item?.headquarters?.country || "",
        pincode:
          item?.headquarters?.pincode || "",
        latitude: item?.headquarters?.coordinates?.coordinates?.[1] ?? undefined,
        longitude: item?.headquarters?.coordinates?.coordinates?.[0] ?? undefined,
        contactName: item?.contactDetails?.contactName || "",
        phone: item?.contactDetails?.phone || "",
        email: item?.contactDetails?.email || "",
        website: item?.contactDetails?.website || "",
        branches: branchesToText(item?.branches),
        thumbnail: thumbnailFileList,
        images: imagesFileList,
      });
      setIsFormDirty(false);

    } catch (err) {
      if (err.name !== "AbortError" && err.name !== "CanceledError") {
        setPageError(true);
        message.error({
          content: err.response?.data?.message || "Failed to load franchise details",
        });
      }
    } finally {
      setPageLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchFranchise();
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, [id]);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      const clickedDropdown = e.target.closest(
        ".ef-custom-select"
      );

      if (!clickedDropdown) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener(
      "mousedown",
      handleOutsideClick
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleOutsideClick
      );
    };
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isFormDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isFormDirty]);

  const handleBack = () => {
    if (isFormDirty) {
      setShowUnsavedModal(true);
    } else {
      navigate("/my-franchises");
    }
  };

  // ── Submit — mirrors AddFranchise onFinish + backend updateFranchise ──────
  const onFinish = async (values) => {

    if (!values.locality?.trim()) {
      message.error("Locality is required");
      return;
    }

    if (values.latitude == null || values.longitude == null) {
      message.error("Latitude and Longitude are required");
      return;
    }

    if (thumbnailList.length === 0) {
      message.error("Please upload a thumbnail image before updating");
      return;
    }
    if (fileList.length === 0) {
      message.error("Please upload at least one gallery image before updating");
      return;
    }
    if (fileList.length > 5) {
      message.error("Maximum 5 images allowed");
      return;
    }

    try {
      setLoading(true);

      // Parse branches (same format as AddFranchise)
      const branches = values.branches
        ? values.branches
          .split("\n")
          .map((line) => {
            const parts = line.split(",");
            if (parts.length !== 3) return null;

            const [branchName, city, state] = parts.map((item) => item.trim());

            if (!branchName || !city || !state) return null;

            return { branchName, city, state };
          })
          .filter(Boolean)
        : [];


      // Normalize website (same as AddFranchise)
      let normalizedWebsite = values.website || "";
      if (normalizedWebsite && !/^https?:\/\//i.test(normalizedWebsite)) {
        normalizedWebsite = `https://${normalizedWebsite}`;
      }

      const data = {
        title: values.brandName,
        description: values.description,
        franchiseDetails: {
          brandName: values.brandName,
          category: showCustomCategory ? customCategory : values.category,
          investmentRequired: Number(values.investmentRequired),
          franchiseFee: Number(values.franchiseFee || 0),
          royaltyFee: Number(values.royaltyFee || 0),
          areaRequired: values.areaRequired ? Number(values.areaRequired) : undefined,
          outletCount: Number(values.outletCount || 1),
        },
        headquarters: {
          locality: values.locality,
          city: values.city,
          state: values.state,
          country: values.country,
          pincode: values.pincode,

          coordinates: {
            type: "Point",
            coordinates: [
              Number(values.longitude),
              Number(values.latitude),
            ],
          },
        },
        contactDetails: {
          contactName: values.contactName,
          email: values.email,
          phone: values.phone,
          website: normalizedWebsite,
        },
        branches,
      };

      // Upload new thumbnail if changed
      const uploadedThumb = thumbnailList[0]?.originFileObj
        ? await uploadToCloudinary(thumbnailList[0].originFileObj, "franchises")
        : null;

      // Upload new gallery images
      const newFilesToUpload = fileList.filter((f) => f.originFileObj);
      const uploadedImages = newFilesToUpload.length
        ? await uploadMultipleToCloudinary(
          newFilesToUpload.map((f) => f.originFileObj),
          "franchises"
        )
        : [];

      await API.put(`/franchises/${id}`, {
        ...data,

        thumbnail: uploadedThumb?.url || thumbnailList[0]?.url,

        images: [
          ...fileList
            .filter(f => !f.originFileObj)
            .map(f => f.url),

          ...uploadedImages.map(img => img.url),
        ],
      });
      setIsFormDirty(false);
      message.success({ content: "Franchise updated successfully" });
      setRemovedImages([]);
      setRemovedThumbnail(false);
      setTimeout(() => navigate("/my-franchises"), 500);
    } catch (err) {
      message.error({
        content: err.response?.data?.message || "Something went wrong. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const isMobile = windowWidth <= 768;

  if (!pageLoading && pageError) {
    return (
      <div className="ef-page">
        <div className="ef-container">
          <Card className="ef-card">
            <div className="ef-error-state">
              <ExclamationCircleOutlined className="ef-error-icon" />
              <h2>Failed to load franchise details</h2>
              <p>Something went wrong while fetching the data. Please try again.</p>
              <Button type="primary" onClick={fetchFranchise}>
                Retry
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="ef-page">
      <div className="ef-container">
        <Spin
          spinning={pageLoading || stepChanging}
          description={stepChanging ? "Loading step..." : "Loading franchise details..."}
        >
          {/* Unsaved changes modal */}
          <Modal
            title={
              <Space>
                <ExclamationCircleOutlined style={{ color: "#faad14" }} />
                <span>Unsaved Changes</span>
              </Space>
            }
            open={showUnsavedModal}
            onOk={() => { setShowUnsavedModal(false); navigate("/my-franchises"); }}
            onCancel={() => setShowUnsavedModal(false)}
            okText="Leave"
            cancelText="Stay and Continue Editing"
            okButtonProps={{ danger: true }}
            width={isMobile ? "90%" : 520}
          >
            <p>You have unsaved changes. Are you sure you want to leave?</p>
            <Text type="secondary">Your changes will be lost if you don't save them.</Text>
          </Modal>

          <Card className="ef-card">
            {/* Header */}
            <div className="ef-card-header">
              <div className="ef-card-header-left">
                <Tag className="ef-edit-badge">Editing Mode</Tag>
                <h1 className="ef-title">Edit Franchise</h1>
                <p className="ef-subtitle">Update your franchise listing details</p>
              </div>
              <Button
                icon={<CloseOutlined />}
                className="ef-back-btn"
                onClick={handleBack}
                aria-label="Go back"
              >
                {!isMobile && "Back to Listing"}
              </Button>
            </div>

            {/* Stepper — same structure as AddFranchise */}
            <div className="ef-stepper">
              {steps.map((step, i) => (
                <div
                  key={i}
                  className={`ef-step ${current === i ? "active" : ""} ${current > i ? "done" : ""}`}
                  onClick={() => goToStep(i)}
                  onKeyDown={(e) => handleKeyDown(e, () => goToStep(i))}
                  role="listitem"
                  tabIndex={0}
                  aria-label={`Step ${i + 1}: ${step}`}
                >
                  <div className="ef-step-line-left" />
                  <div className="ef-step-inner">
                    <div className="ef-circle">
                      {current > i ? (
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      ) : (
                        i + 1
                      )}
                    </div>
                    <span className="ef-step-label">{step}</span>
                  </div>
                  <div className="ef-step-line-right" />
                </div>
              ))}
            </div>

            {/* Progress bar */}
            <div className="ef-progress-bar">
              <div
                className="ef-progress-fill"
                style={{ width: `${(current / (steps.length - 1)) * 100}%` }}
              />
            </div>
            <div className="ef-progress-label">
              Step {current + 1} of {steps.length} — {steps[current]}
            </div>

            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              onValuesChange={() => setIsFormDirty(true)}
              scrollToFirstError
              validateTrigger="onBlur"
              className="ef-form"
            >
              {/* Step 0: Brand */}
              <div style={getStepStyle(0)}>
                <Form.Item
                  name="brandName"
                  label="Brand Name"
                  rules={[
                    { required: true, message: "Please enter your brand name" },
                    { min: 3, message: "Brand name must be at least 3 characters long" },
                    {
                      pattern: /^(?=.*[A-Za-z])[A-Za-z0-9][A-Za-z0-9\s.&'-]*$/,
                      message:
                        "Brand name must start with a letter and can contain numbers, spaces, . & ' -",
                    },
                  ]}
                >
                  <Input placeholder="e.g. KFC, Pizza Hut, McDonald's" />
                </Form.Item>

                <Form.Item
                  name="category"
                  label="Category"
                  rules={[{ required: true, message: "Please select a category" }]}
                >
                  <div
                    className={`ef-custom-select ${openDropdown === "category"
                        ? "active"
                        : ""
                      }`}
                  >
                    <div
                      className="ef-custom-select-trigger"
                      onClick={() =>
                        setOpenDropdown(
                          openDropdown === "category"
                            ? null
                            : "category"
                        )
                      }
                    >
                      {form.getFieldValue("category")
                        ? form
                          .getFieldValue("category")
                          .charAt(0)
                          .toUpperCase() +
                        form
                          .getFieldValue("category")
                          .slice(1)
                        : "Select category"}

                      <span className="ef-custom-arrow">
                        ⌄
                      </span>
                    </div>

                    {openDropdown === "category" && (
                      <div className="ef-custom-dropdown">
                        {CATEGORY_OPTIONS.map((item) => (
                          <div
                            key={item}
                            className="ef-custom-dropdown-item"
                            onClick={() => {
                              form.setFieldValue(
                                "category",
                                item
                              );

                              if (item === "other") {
                                setShowCustomCategory(true);
                              } else {
                                setShowCustomCategory(false);
                                setCustomCategory("");
                              }

                              setOpenDropdown(null);
                            }}
                          >
                            {item.charAt(0).toUpperCase() +
                              item.slice(1)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </Form.Item>

                {showCustomCategory && (
                  <Form.Item
                    name="customCategory"
                    label="Enter Custom Category"
                    rules={[
                      { required: true, message: "Please enter custom category" },
                      { min: 2, message: "Category must be at least 2 characters" },
                      {
                        pattern: /^[A-Za-z][A-Za-z\s&-]*$/,
                        message: "Start with letter, can include letters, spaces, & and -",
                      },
                    ]}
                    style={{ marginTop: -8 }}
                  >
                    <Input
                      placeholder="e.g. Restaurant, Café, Fitness"
                      value={customCategory}
                      onChange={(e) => {
                        setCustomCategory(e.target.value);
                        form.setFieldValue("category", e.target.value);
                      }}
                    />
                  </Form.Item>
                )}

                <Form.Item
                  name="description"
                  label="Description"
                  rules={[
                    { required: true, message: "Please provide a description" },
                    { min: 20, message: "Description should be at least 20 characters" },
                  ]}
                >
                  <TextArea
                    rows={5}
                    placeholder="Describe the franchise model, opportunity, support system, training programs, and market fit..."
                  />
                </Form.Item>
              </div>

              {/* Step 1: Financial */}
              <div style={getStepStyle(1)}>
                <Row gutter={[16, 0]}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="investmentRequired"
                      label="Investment Required (₹)"
                      rules={[
                        { required: true, message: "Please enter investment amount" },
                        { type: "number", min: 10000, message: "Investment must be at least ₹10,000" },
                      ]}
                    >
                      <InputNumber style={{ width: "100%" }} placeholder="e.g. 500000" min={10000} />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="franchiseFee"
                      label="Franchise Fee (₹)"
                      rules={[
                        { required: true, message: "Please enter franchise fee" },
                        { type: "number", min: 0, message: "Franchise fee cannot be negative" },
                      ]}
                    >
                      <InputNumber style={{ width: "100%" }} placeholder="e.g. 100000" min={0} />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="royaltyFee"
                      label="Royalty Fee (%)"
                      rules={[
                        { required: true, message: "Please enter royalty fee" },
                        { type: "number", min: 0, max: 100, message: "Enter valid percentage (0-100)" },
                      ]}
                    >
                      <InputNumber style={{ width: "100%" }} placeholder="e.g. 5" min={0} />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="areaRequired"
                      label="Area Required (sq ft)"
                      rules={[
                        { required: true, message: "Please enter area required" },
                        { type: "number", min: 50, message: "Area must be at least 50 sq ft" },
                      ]}
                    >
                      <InputNumber style={{ width: "100%" }} placeholder="e.g. 500" min={50} />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="outletCount"
                      label="Outlet Count"
                      rules={[
                        { required: true, message: "Please enter outlet count" },
                        { type: "number", min: 1, message: "Outlet count must be at least 1" },
                      ]}
                    >
                      <InputNumber style={{ width: "100%" }} placeholder="e.g. 10" min={1} />
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              {/* Step 2: Location (Headquarters) */}
              <div style={getStepStyle(2)}>
                <Form.Item
                  name="locality"
                  label="Locality"
                  rules={[
                    {
                      required: true,
                      message: "Please select locality",
                    },

                    {
                      pattern:
                        /^(?=.*[A-Za-z])[A-Za-z0-9\s.,#&()'/-]+$/,
                      message: "Invalid locality",
                    },
                  ]}
                >
                  <LocationField
                    value={form.getFieldValue("locality")}
                    form={form}
                    message={message}
                    setAutoDetecting={setAutoDetecting}
                    onChange={(value) => {
                      form.setFieldsValue({
                        locality: value,
                      });
                    }}
                  />
                </Form.Item>

                <Row gutter={[16, 0]}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="city"
                      label="City"
                      rules={[
                        {
                          required: true,
                          message: "Please enter the city",
                        },

                        {
                          pattern: /^[A-Za-z\s.-]+$/,
                          message:
                            "City name should contain only letters",
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="pincode"
                      label={
                        <span >
                          Pincode
                        </span>
                      }
                      required
                      rules={[
                        {
                          required: true,
                          message: "Please enter pincode",
                        },

                        {
                          pattern: /^[0-9]{4,10}$/,
                          message: "Invalid pincode",
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={[16, 0]}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="state"
                      label="State"
                      rules={[
                        { required: true, message: "Please enter the State" },
                        {
                          pattern: /^[A-Za-z\s.-]+$/,
                          message: "State name should contain only letters",
                        },
                      ]}
                    >
                      <Input placeholder="e.g. Karnataka" />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="country"
                      label="Country"
                      rules={[
                        { required: true, message: "Please enter the country" },
                        {
                          pattern: /^[A-Za-z\s.-]+$/,
                          message: "Country name should contain only letters",
                        },
                      ]}
                    >
                      <Input placeholder="e.g. India" />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={[16, 0]}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="latitude"
                      label="Latitude"
                      rules={[
                        { required: true, message: "Latitude is required" },
                        {
                          validator: (_, value) => {
                            if (value === undefined || value === null || value === "")
                              return Promise.reject("Latitude is required");
                            const str = value.toString();
                            if ((str.match(/\./g) || []).length > 1)
                              return Promise.reject("Only one decimal point allowed");
                            const parts = str.split(".");
                            if (parts[1] && parts[1].length > 6)
                              return Promise.reject("Max 6 digits allowed after decimal");
                            if (typeof value !== "number" || Number.isNaN(value))
                              return Promise.reject("Enter a valid latitude");
                            if (value < -90 || value > 90)
                              return Promise.reject("Latitude must be between -90 and 90");
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <InputNumber
                        style={{ width: "100%" }}
                        min={-90}
                        max={90}
                        step={0.000001}
                        placeholder="e.g. 12.971891"
                        onBlur={() => {
                          const val = form.getFieldValue("latitude");
                          if (typeof val === "number") {
                            form.setFieldsValue({ latitude: Number(val.toFixed(6)) });
                          }
                        }}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="longitude"
                      label="Longitude"
                      rules={[
                        { required: true, message: "Longitude is required" },
                        {
                          validator: (_, value) => {
                            if (value === undefined || value === null || value === "")
                              return Promise.reject("Longitude is required");
                            const str = value.toString();
                            if ((str.match(/\./g) || []).length > 1)
                              return Promise.reject("Only one decimal point allowed");
                            const parts = str.split(".");
                            if (parts[1] && parts[1].length > 6)
                              return Promise.reject("Max 6 digits allowed after decimal");
                            if (typeof value !== "number" || Number.isNaN(value))
                              return Promise.reject("Enter a valid longitude");
                            if (value < -180 || value > 180)
                              return Promise.reject("Longitude must be between -180 and 180");
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <InputNumber
                        style={{ width: "100%" }}
                        min={-180}
                        max={180}
                        step={0.000001}
                        placeholder="e.g. 77.594563"
                        onBlur={() => {
                          const val = form.getFieldValue("longitude");
                          if (typeof val === "number") {
                            form.setFieldsValue({ longitude: Number(val.toFixed(6)) });
                          }
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              {/* Step 3: Contact */}
              <div style={getStepStyle(3)}>
                <Form.Item
                  name="contactName"
                  label="Contact Person Name"
                  rules={[
                    { required: true, message: "Please enter contact name" },
                    {
                      pattern: /^[A-Za-z\s.-]+$/,
                      message: "Name can only contain letters and basic characters",
                    },
                  ]}
                >
                  <Input placeholder="e.g. Rajesh Kumar" />
                </Form.Item>

                <Row gutter={[16, 0]}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="phone"
                      label="Phone Number"
                      rules={[
                        { required: true, message: "Please enter a phone number" },
                        {
                          pattern: /^[6-9]\d{9}$/,
                          message: "Enter a valid 10-digit Indian phone number",
                        },
                      ]}
                    >
                      <Input maxLength={10} placeholder="e.g. 9876543210" />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="email"
                      label="Email Address"
                      rules={[
                        { required: true, message: "Please enter your email address" },
                        { type: "email", message: "Enter a valid email address" },
                      ]}
                    >
                      <Input placeholder="e.g. franchise@brand.com" />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  name="website"
                  label="Website"
                  rules={[
                    {
                      pattern: /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/.*)?$/i,
                      message: "Enter a valid website URL",
                    },
                  ]}
                >
                  <Input placeholder="e.g. www.yourbrand.com" />
                </Form.Item>
              </div>

              {/* Step 4: Branches — same format as AddFranchise */}
              <div style={getStepStyle(4)}>
                <Form.Item
                  name="branches"
                  label="Branches"
                  extra="Format: Branch Name, City, State (one per line)"
                  rules={[
                    {
                      validator: (_, value) => {
                        if (!value || !value.trim()) return Promise.resolve();
                        const lines = value.split("\n").filter((l) => l.trim());
                        if (lines.length === 0) return Promise.resolve();
                        for (let line of lines) {
                          const parts = line.split(",").map((p) => p.trim());
                          if (parts.length !== 3) {
                            return Promise.reject(
                              "Each line must have exactly 3 parts: Branch Name, City, State"
                            );
                          }
                          const [branch, city, state] = parts;
                          if (!branch || !city || !state) {
                            return Promise.reject(
                              "Branch Name, City, and State are all required"
                            );
                          }
                          if (
                            !/^[A-Za-z0-9\s.&'-]+$/.test(branch) ||
                            !/^[A-Za-z\s.-]+$/.test(city) ||
                            !/^[A-Za-z\s.-]+$/.test(state)
                          ) {
                            return Promise.reject(
                              "Invalid characters detected. Use only letters, numbers, spaces, . & ' -"
                            );
                          }
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <TextArea
                    rows={5}
                    placeholder={
                      "Koramangala, Bengaluru, Karnataka\nIndiranagar, Bengaluru, Karnataka\nWhitefield, Bengaluru, Karnataka"
                    }
                  />
                </Form.Item>
              </div>

              {/* Step 5: Images */}
              <div style={getStepStyle(5)}>
                {/* Thumbnail */}
                <Form.Item
                  name="thumbnail"
                  label={
                    <span className="ef-label-with-badge">
                      Thumbnail
                      <span className="ef-badge-primary">Main Image</span>
                    </span>
                  }
                  valuePropName="fileList"
                  getValueFromEvent={normalizeUpload}
                  rules={[{ required: true, message: "Please upload a thumbnail image" }]}
                >
                  <Upload.Dragger
                    maxCount={1}
                    accept="image/png,image/jpeg"
                    className="ef-upload"
                    fileList={thumbnailList}
                    beforeUpload={(file) => {
                      const isValidType = ["image/png", "image/jpeg"].includes(file.type);
                      if (!isValidType) {
                        message.error("Only PNG, JPEG files are allowed");
                        return Upload.LIST_IGNORE;
                      }
                      return false;
                    }}
                    onChange={({ fileList: newFileList }) => {
                      if (newFileList.length > 0) {
                        const file = newFileList[0];
                        setThumbnailList([
                          {
                            uid: "thumb-" + Date.now(),
                            name: file.name,
                            url: file.originFileObj
                              ? URL.createObjectURL(file.originFileObj)
                              : file.url,
                            originFileObj: file.originFileObj,
                            status: "done",
                          },
                        ]);
                        setRemovedThumbnail(false);
                      } else {
                        setThumbnailList([]);
                        setRemovedThumbnail(true);
                      }
                    }}
                    onRemove={() => {
                      setThumbnailList([]);
                      setRemovedThumbnail(true);
                    }}
                  >
                    <div className="ef-upload-inner">
                      <p className="ef-upload-title">Upload Thumbnail</p>
                      <p className="ef-upload-hint">
                        This is the first image users see · PNG, JPEG
                      </p>
                    </div>
                  </Upload.Dragger>
                </Form.Item>

                {/* Gallery images */}
                <Form.Item
                  name="images"
                  valuePropName="fileList"
                  label={
                    <span className="ef-label-with-badge">
                      Franchise Images
                      <span className="ef-badge-secondary">Up to 5</span>
                    </span>
                  }
                  getValueFromEvent={normalizeUpload}
                  rules={[
                    { required: true, message: "Please upload at least one image" },
                    {
                      validator: () => {
                        if (fileList.length === 0)
                          return Promise.reject("Please upload at least one image");
                        if (fileList.length > 5)
                          return Promise.reject("Maximum 5 images allowed");
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Upload.Dragger
                    className="ef-upload"
                    maxCount={5}
                    accept="image/png,image/jpeg"
                    multiple
                    fileList={fileList}
                    beforeUpload={(file) => {
                      const isValidType = ["image/png", "image/jpeg"].includes(file.type);
                      if (!isValidType) {
                        message.error("Only PNG, JPEG files are allowed");
                        return Upload.LIST_IGNORE;
                      }
                      return false;
                    }}
                    onChange={({ fileList: newFileList }) => {
                      setFileList(newFileList);
                    }}
                    onRemove={(file) => {
                      if (file.url && !file.originFileObj) {
                        setRemovedImages((prev) => [...prev, file.url]);
                      }
                      return true;
                    }}
                  >
                    <div className="ef-upload-inner">
                      <p className="ef-upload-title">Upload Franchise Images</p>
                      <p className="ef-upload-hint">Add up to 5 images · PNG, JPEG</p>
                    </div>
                  </Upload.Dragger>
                </Form.Item>
              </div>

              {/* Navigation buttons — mirrors AddFranchise */}
              <div className="ef-btns">
                {current > 0 && (
                  <Button
                    onClick={prev}
                    className="ef-btn-prev"
                    icon={<ArrowLeftOutlined />}
                    disabled={autoDetecting}
                  >
                    Previous
                  </Button>
                )}
                <div className="ef-btns-right">
                  {current < steps.length - 1 && (
                    <Button
                      type="primary"
                      onClick={next}
                      className="ef-btn-next"
                      disabled={autoDetecting}
                    >
                      Next →
                    </Button>
                  )}
                  {current === steps.length - 1 && (
                    <Button
                      type="primary"
                      onClick={async () => {
                        if (thumbnailList.length === 0) {
                          message.error("Please upload a thumbnail image");
                          return;
                        }
                        if (fileList.length === 0) {
                          message.error("Please upload at least one image");
                          return;
                        }
                        form.submit();
                      }}
                      loading={loading}
                      disabled={loading}
                      icon={<SaveOutlined />}
                      className="ef-btn-submit"
                    >
                      {loading ? "Updating..." : "Update Franchise"}
                    </Button>
                  )}
                </div>
              </div>
            </Form>
          </Card>
        </Spin>
      </div>
    </div>
  );
}

export default EditFranchise;